import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  type GridColDef,
  type GridRowId,
  type GridSelectionModel,
} from "@mui/x-data-grid-pro";
import { ResponsiveTable } from "@/components/Responsive";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface Props {
  title: string;
  columns: GridColDef[];
  rows: ReadonlyArray<{ id: GridRowId }>;
  selectedRowIds: GridSelectionModel;
  handleRowSelectionChange: (newSelectionModel: GridSelectionModel) => void;
  loading?: boolean;
  emptyMessage?: string;
}

// チェックボックスは見た目を変えずにタップ領域を 44px にする（padding 10px + アイコン 24px）
const checkboxSx = { p: 1.25, m: -1.25 };

/**
 * DataGrid の列定義から SP のカードに出す値を作る（PC の renderCell と同じ表示に揃える）
 * @param {string} field 列のフィールド名
 * @param {unknown} value セルの値
 * @returns {React.ReactNode}
 */
const formatCell = (field: string, value: unknown): React.ReactNode => {
  if (value === undefined || value === null) return undefined;
  if (field === "hitCount") {
    const count = value as number;
    // 0件の場合は赤色、1件以上の場合は青色（PC の hit-count-cell と同じ色）
    return (
      <Box
        component="span"
        sx={{ color: count === 0 ? "#d32f2f" : "#1976d2", fontWeight: "bold" }}
      >
        {`${formatNumberWithComma(count)}件`}
      </Box>
    );
  }
  if (field === "chiban" || field === "kaokuNumber") {
    return NYHalfWidthNumberToFillWidthNumber(value as string);
  }
  return String(value);
};

/**
 * 地番・家屋番号検索の一覧の SP 表示（1行 = 1カード + 行選択）。
 * 選択 state は PC の DataGrid と共有するため、親から受け取る。
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const SelectableCardList: React.FC<Props> = (props) => {
  const {
    title,
    columns,
    rows,
    selectedRowIds,
    handleRowSelectionChange,
    loading = false,
    emptyMessage,
  } = props;

  const selectedCount = rows.filter((row) =>
    selectedRowIds.includes(row.id)
  ).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  const toggleRow = (id: GridRowId, checked: boolean): void => {
    handleRowSelectionChange(
      checked
        ? [...selectedRowIds, id]
        : selectedRowIds.filter((selectedId) => selectedId !== id)
    );
  };

  // 行を読み上げで区別できるよう、所在と末尾の地番・家屋番号をチェックボックスのラベルにする
  const keyField = [...columns]
    .reverse()
    .find((c) => c.field === "chiban" || c.field === "kaokuNumber")?.field;

  const cardRows = rows.map((row) => {
    const values = row as unknown as Record<string, unknown>;
    const rowLabel = `${String(values.location ?? "")}${
      keyField
        ? NYHalfWidthNumberToFillWidthNumber(String(values[keyField] ?? ""))
        : ""
    }`;
    return [
      <Checkbox
        key="select"
        sx={checkboxSx}
        checked={selectedRowIds.includes(row.id)}
        onChange={(e) => {
          toggleRow(row.id, e.target.checked);
        }}
        inputProps={{ "aria-label": `${title} ${rowLabel}を選択` }}
      />,
      ...columns.map((column) =>
        formatCell(column.field, values[column.field])
      ),
    ];
  });

  return (
    <Box>
      {rows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Checkbox
                sx={checkboxSx}
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                onChange={(e) => {
                  handleRowSelectionChange(
                    e.target.checked ? rows.map((row) => row.id) : []
                  );
                }}
              />
            }
            label={<Box sx={{ ml: 1 }}>すべて選択</Box>}
          />
          <Typography variant="body2" color="text.secondary">
            {`${formatNumberWithComma(selectedCount)} / ${formatNumberWithComma(
              rows.length
            )}件 選択中`}
          </Typography>
        </Box>
      )}
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <ResponsiveTable
        title={title}
        headers={["選択", ...columns.map((c) => c.headerName ?? c.field)]}
        rows={cardRows}
        emptyMessage={loading ? "検索中です" : emptyMessage}
      />
    </Box>
  );
};
